import { asArray, isRecord, normalizeContacts, normalizeGroup, normalizeGroups, normalizeMessage, normalizeMessages } from './safe-data.js';
import { isTrackedRequestValue, trackRequestValue, unwrapRequestValue } from './idb-result.js';
import {
  createVaultMetadata, unlockFromMetadata, rewrapVaultMetadata,
  isVaultUnlocked, lockVaultMemory, sealJson, openJson, sealBytes, openBytes, localTag,
  wrapMasterKeyWithExternalSecret, unlockFromExternalSecret, wrapCurrentMasterKeyWithCode,
} from './local-vault.js';
import { toB64, fromB64, requireSodium } from './crypto.js';

// storage.js — v4.9.0 : données sensibles chiffrées au repos.
// Les stores v1-v3 restent présents uniquement pour une migration atomique ;
// après activation du coffre ils sont vides. Les stores *_secure ne contiennent
// en clair que des identifiants techniques/tags keyed non réversibles.
const DB_NAME = 'messagerie-e2e';
const DB_VERSION = 4;
const LEGACY_STORES = ['identity', 'contacts', 'messages', 'settings', 'groups'];
const SECURE_STORES = ['identity_secure', 'contacts_secure', 'messages_secure', 'settings_secure', 'groups_secure'];
const VAULT_STORE = 'vault';
const AUTO_SECRET_KEY = 'messagerie:vault-auto-secret:v1';

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('identity')) db.createObjectStore('identity', { keyPath: 'pseudo' });
      if (!db.objectStoreNames.contains('contacts')) db.createObjectStore('contacts', { keyPath: 'pseudo' });
      if (!db.objectStoreNames.contains('messages')) {
        const st = db.createObjectStore('messages', { keyPath: 'localId', autoIncrement: true });
        st.createIndex('byPeer', 'peer');
      }
      if (!db.objectStoreNames.contains('settings')) db.createObjectStore('settings', { keyPath: 'key' });
      if (!db.objectStoreNames.contains('groups')) db.createObjectStore('groups', { keyPath: 'groupId' });

      if (!db.objectStoreNames.contains(VAULT_STORE)) db.createObjectStore(VAULT_STORE, { keyPath: 'key' });
      if (!db.objectStoreNames.contains('identity_secure')) db.createObjectStore('identity_secure', { keyPath: 'slot' });
      if (!db.objectStoreNames.contains('contacts_secure')) db.createObjectStore('contacts_secure', { keyPath: 'tag' });
      if (!db.objectStoreNames.contains('messages_secure')) {
        const st = db.createObjectStore('messages_secure', { keyPath: 'localId', autoIncrement: true });
        st.createIndex('byPeerTag', 'peerTag');
      }
      if (!db.objectStoreNames.contains('settings_secure')) db.createObjectStore('settings_secure', { keyPath: 'tag' });
      if (!db.objectStoreNames.contains('groups_secure')) db.createObjectStore('groups_secure', { keyPath: 'tag' });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function tx(store, mode, fn) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const t = db.transaction(store, mode);
    let result;
    try { result = fn(t.objectStore(store)); }
    catch (error) { try { t.abort(); } catch (_) {} reject(error); return; }
    const tracked = isTrackedRequestValue(result);
    t.oncomplete = () => resolve(tracked ? unwrapRequestValue(result) : result);
    t.onerror = () => reject(t.error || new Error(`Transaction IndexedDB échouée (${store}).`));
    t.onabort = () => reject(t.error || new Error(`Transaction IndexedDB annulée (${store}).`));
  });
}
const reqValue = (r) => trackRequestValue(r);
const validStringKey = (v) => typeof v === 'string' && v.trim().length > 0;
const validMessageKey = (v) => (typeof v === 'number' && Number.isFinite(v)) || validStringKey(v);
function requireUnlocked() {
  if (!isVaultUnlocked()) { const e = new Error('Coffre local verrouillé.'); e.name = 'VaultLockedError'; throw e; }
}
async function countStore(store) { return Number(await tx(store, 'readonly', (s) => reqValue(s.count())) || 0); }

// ---------------------------------------------------------------------------
// Coffre / migration
// ---------------------------------------------------------------------------
async function getVaultMeta() { return (await tx(VAULT_STORE, 'readonly', (s) => reqValue(s.get('meta')))) || null; }
async function countLegacyPlaintext() {
  const legacy = {};
  for (const name of LEGACY_STORES) legacy[name] = await countStore(name);
  return { ...legacy, total: Object.values(legacy).reduce((a, b) => a + b, 0) };
}
function readAutoSecret() {
  try {
    const raw = globalThis.localStorage?.getItem(AUTO_SECRET_KEY) || '';
    return raw ? fromB64(raw) : null;
  } catch (_) { return null; }
}
function writeAutoSecret(secret) {
  try { globalThis.localStorage?.setItem(AUTO_SECRET_KEY, toB64(secret)); return true; }
  catch (_) { throw new Error('Impossible d’enregistrer le déverrouillage automatique sur cet appareil.'); }
}
function clearAutoSecret() { try { globalThis.localStorage?.removeItem(AUTO_SECRET_KEY); } catch (_) {} }
function newAutoSecret() { return requireSodium().randombytes_buf(32); }

export async function getVaultStatus() {
  const meta = await getVaultMeta(); const legacy = await countLegacyPlaintext();
  const autoSecret = readAutoSecret();
  return {
    configured: !!meta, unlocked: isVaultUnlocked(), legacyPlaintext: legacy.total,
    createdAt: Number(meta?.createdAt || 0), kdf: meta?.kdf || '', biometric: !!meta?.webauthn?.credentialId,
    autoUnlock: !!(meta?.automatic?.wrap && autoSecret), automaticConfigured: !!meta?.automatic?.wrap,
  };
}
export async function initializeVault(code) {
  if (await getVaultMeta()) throw new Error('Le coffre local existe déjà.');
  const meta = createVaultMetadata(code);
  await tx(VAULT_STORE, 'readwrite', (s) => s.put(meta));
  try {
    await migrateLegacyData();
    return true;
  } catch (error) {
    // La migration data est transactionnelle : si elle échoue, le plaintext
    // legacy est intact. On retire aussi le nouveau meta pour permettre un
    // nouvel essai immédiat avec un autre code au lieu de coincer l'écran setup.
    try { await tx(VAULT_STORE, 'readwrite', (s) => s.delete('meta')); } catch (_) {}
    lockVaultMemory();
    throw error;
  }
}
export async function initializeAutomaticVault() {
  if (await getVaultMeta()) throw new Error('Le stockage local est déjà configuré.');
  // Le code interne aléatoire ne sert jamais à l’utilisateur : la DEK est
  // immédiatement enveloppée une seconde fois avec un secret local automatique.
  const internal = toB64(requireSodium().randombytes_buf(24));
  let meta = createVaultMetadata(internal);
  const secret = newAutoSecret();
  try {
    const wrap = wrapMasterKeyWithExternalSecret(secret, 'device-auto-vault');
    meta = { ...meta, automatic: { v: 1, createdAt: Date.now(), wrap } };
    await tx(VAULT_STORE, 'readwrite', (st) => st.put(meta));
    writeAutoSecret(secret);
    await migrateLegacyData();
    return true;
  } catch (error) {
    try { await tx(VAULT_STORE, 'readwrite', (st) => st.delete('meta')); } catch (_) {}
    clearAutoSecret(); lockVaultMemory(); throw error;
  } finally { try { requireSodium().memzero(secret); } catch (_) {} }
}
export async function unlockVaultAutomatically() {
  const meta = await getVaultMeta(); const secret = readAutoSecret();
  if (!meta?.automatic?.wrap || !secret) throw new Error('Déverrouillage automatique indisponible.');
  try { unlockFromExternalSecret(meta.automatic.wrap, secret, 'device-auto-vault'); }
  finally { try { requireSodium().memzero(secret); } catch (_) {} }
  await migrateLegacyData(); return true;
}
export async function enableVaultAutomaticUnlock() {
  requireUnlocked();
  const meta = await getVaultMeta(); if (!meta) throw new Error('Aucun coffre local configuré.');
  const secret = newAutoSecret();
  try {
    const wrap = wrapMasterKeyWithExternalSecret(secret, 'device-auto-vault');
    await tx(VAULT_STORE, 'readwrite', (st) => st.put({ ...meta, automatic: { v: 1, createdAt: Date.now(), wrap } }));
    try { writeAutoSecret(secret); }
    catch (error) { await tx(VAULT_STORE, 'readwrite', (st) => st.put(meta)); throw error; }
    return true;
  } finally { try { requireSodium().memzero(secret); } catch (_) {} }
}
export async function disableVaultAutomaticUnlock(newCode) {
  requireUnlocked();
  const meta = await getVaultMeta(); if (!meta) throw new Error('Aucun coffre local configuré.');
  // On crée volontairement un nouveau wrapper de code connu de l’utilisateur
  // avant de retirer l’auto-unlock : impossible de se verrouiller dehors.
  const strong = wrapCurrentMasterKeyWithCode(meta, newCode);
  const next = { ...strong }; delete next.automatic;
  await tx(VAULT_STORE, 'readwrite', (st) => st.put(next));
  clearAutoSecret(); return true;
}

export async function unlockVault(code) {
  const meta = await getVaultMeta();
  if (!meta) throw new Error('Aucun coffre local configuré.');
  unlockFromMetadata(meta, code);
  await migrateLegacyData();
  return true;
}
export async function changeVaultCode(oldCode, newCode) {
  const meta = await getVaultMeta();
  if (!meta) throw new Error('Aucun coffre local configuré.');
  const next = rewrapVaultMetadata(meta, oldCode, newCode);
  await tx(VAULT_STORE, 'readwrite', (s) => s.put(next));
  return true;
}
export function lockVault() { lockVaultMemory(); }
export async function getVaultWebAuthnConfig() {
  const meta = await getVaultMeta();
  const w = meta?.webauthn;
  return w?.credentialId && w?.prfSalt ? { credentialId: String(w.credentialId), prfSalt: String(w.prfSalt), createdAt: Number(w.createdAt || 0) } : null;
}
export async function enableVaultWebAuthn(config, prfSecret) {
  requireUnlocked();
  const meta = await getVaultMeta();
  if (!meta) throw new Error('Aucun coffre local configuré.');
  if (!config?.credentialId || !config?.prfSalt) throw new Error('Configuration WebAuthn invalide.');
  const wrap = wrapMasterKeyWithExternalSecret(prfSecret, 'webauthn-prf');
  const next = { ...meta, webauthn: { v: 1, credentialId: String(config.credentialId), prfSalt: String(config.prfSalt), createdAt: Number(config.createdAt || Date.now()), wrap } };
  await tx(VAULT_STORE, 'readwrite', (s) => s.put(next));
  return true;
}
export async function disableVaultWebAuthn() {
  requireUnlocked();
  const meta = await getVaultMeta(); if (!meta) return false;
  const next = { ...meta }; delete next.webauthn;
  await tx(VAULT_STORE, 'readwrite', (s) => s.put(next)); return true;
}
export async function unlockVaultWithWebAuthn(prfSecret) {
  const meta = await getVaultMeta();
  if (!meta?.webauthn?.wrap) throw new Error('Déverrouillage biométrique non configuré.');
  unlockFromExternalSecret(meta.webauthn.wrap, prfSecret, 'webauthn-prf');
  await migrateLegacyData(); return true;
}
export async function auditVaultAtRest() {
  const legacy = await countLegacyPlaintext(); const secure = {};
  for (const name of SECURE_STORES) secure[name] = await countStore(name);
  const meta = await getVaultMeta();
  return { configured: !!meta, unlocked: isVaultUnlocked(), automatic: !!(meta?.automatic?.wrap && readAutoSecret()), plaintextLegacyRows: legacy.total, legacy, encryptedRows: Object.values(secure).reduce((a, b) => a + b, 0), secure };
}

function sealTagged(scope, tagNamespace, logicalKey, value) {
  const tag = localTag(tagNamespace, logicalKey);
  const sealed = sealJson(scope, '', value);
  return { tag, nonce: sealed.nonce, box: sealed.box };
}
function openTagged(scope, tagNamespace, record, logicalKey = null) {
  const value = openJson(scope, '', record, null);
  if (logicalKey !== null && record?.tag !== localTag(tagNamespace, logicalKey)) throw new Error('Index local chiffré incohérent.');
  return value;
}

function toU8(value) {
  if (value instanceof Uint8Array) return value;
  if (value instanceof ArrayBuffer) return new Uint8Array(value);
  if (ArrayBuffer.isView(value)) return new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
  return null;
}
function toAb(u8) { return u8.buffer.slice(u8.byteOffset, u8.byteOffset + u8.byteLength); }

async function sealMessageRecord(message, preserveLocalId = true) {
  const safe = normalizeMessage(message);
  if (!safe) throw new Error('Message local invalide.');
  const meta = { ...safe };
  const localId = validMessageKey(meta.localId) ? meta.localId : undefined;
  delete meta.localId;
  let data = meta.data; delete meta.data;
  // Anciennes lignes Blob : migration en ArrayBuffer avant chiffrement.
  if (!data && meta.blob instanceof Blob) data = await meta.blob.arrayBuffer();
  delete meta.blob;
  const sealed = sealJson('message-meta', '', meta);
  const record = { peerTag: localTag('peer', safe.peer), nonce: sealed.nonce, box: sealed.box };
  if (preserveLocalId && localId !== undefined) record.localId = localId;
  if (data) {
    if (Array.isArray(data)) {
      record.binaryKind = 'chunks';
      record.binary = data.map((chunk, i) => {
        const bytes = toU8(chunk); if (!bytes) throw new Error('Morceau média local invalide.');
        return sealBytes('message-binary', `chunk:${i}`, bytes);
      });
    } else {
      const bytes = toU8(data); if (!bytes) throw new Error('Média local invalide.');
      record.binaryKind = 'one'; record.binary = sealBytes('message-binary', 'data', bytes);
    }
  }
  return record;
}
function openMessageMeta(record) {
  const meta = openJson('message-meta', '', record, null);
  if (!meta || record.peerTag !== localTag('peer', meta.peer)) throw new Error('Index de message local incohérent.');
  return { ...meta, localId: record.localId };
}
function openMessageRecord(record) {
  const msg = openMessageMeta(record);
  if (record.binaryKind === 'one' && record.binary) msg.data = toAb(openBytes('message-binary', 'data', record.binary));
  else if (record.binaryKind === 'chunks' && Array.isArray(record.binary)) msg.data = record.binary.map((r, i) => toAb(openBytes('message-binary', `chunk:${i}`, r)));
  return normalizeMessage(msg);
}

async function legacySnapshot() {
  const out = {};
  for (const name of LEGACY_STORES) out[name] = asArray(await tx(name, 'readonly', (s) => reqValue(s.getAll())));
  return out;
}
export async function migrateLegacyData() {
  requireUnlocked();
  const counts = await countLegacyPlaintext();
  if (!counts.total) return { migrated: 0 };
  const legacy = await legacySnapshot();
  const identities = legacy.identity.filter((x) => x?.pseudo && x?.privateKey).map((x) => ({ slot: 'primary', ...sealJson('identity', 'primary', x) }));
  const contacts = normalizeContacts(legacy.contacts).map((c) => sealTagged('contact', 'contact', c.pseudo, c));
  const groups = normalizeGroups(legacy.groups).map((g) => sealTagged('group', 'group', g.groupId, g));
  const settings = legacy.settings.filter((r) => validStringKey(r?.key)).map((r) => sealTagged('setting', 'setting', r.key, r.value));
  const messages = [];
  for (const raw of legacy.messages) { const m = normalizeMessage(raw); if (m) messages.push(await sealMessageRecord(m, true)); }

  const db = await openDb();
  await new Promise((resolve, reject) => {
    const t = db.transaction([...LEGACY_STORES, ...SECURE_STORES], 'readwrite');
    for (const r of identities) t.objectStore('identity_secure').put(r);
    for (const r of contacts) t.objectStore('contacts_secure').put(r);
    for (const r of groups) t.objectStore('groups_secure').put(r);
    for (const r of settings) t.objectStore('settings_secure').put(r);
    for (const r of messages) t.objectStore('messages_secure').put(r);
    // Écriture chiffrée + effacement plaintext dans une seule transaction IDB.
    for (const name of LEGACY_STORES) t.objectStore(name).clear();
    t.oncomplete = resolve;
    t.onerror = () => reject(t.error || new Error('Migration du coffre local échouée.'));
    t.onabort = () => reject(t.error || new Error('Migration du coffre local annulée.'));
  });
  return { migrated: counts.total };
}

// ---------------------------------------------------------------------------
// Identité / contacts / groupes / réglages
// ---------------------------------------------------------------------------
export async function saveIdentity(id) { requireUnlocked(); const s = sealJson('identity', 'primary', id); return tx('identity_secure', 'readwrite', (st) => st.put({ slot: 'primary', ...s })); }
export async function getIdentity() { requireUnlocked(); const r = await tx('identity_secure', 'readonly', (s) => reqValue(s.get('primary'))); return r ? openJson('identity', 'primary', r, null) : null; }
export async function clearIdentity() { requireUnlocked(); return tx('identity_secure', 'readwrite', (s) => s.clear()); }

export async function saveContact(c) { requireUnlocked(); const safe = normalizeContacts([c])[0]; if (!safe) throw new Error('Contact local invalide.'); return tx('contacts_secure', 'readwrite', (s) => s.put(sealTagged('contact', 'contact', safe.pseudo, safe))); }
export async function getContacts() {
  requireUnlocked(); const rows = asArray(await tx('contacts_secure', 'readonly', (s) => reqValue(s.getAll())));
  const values = [];
  for (const row of rows) {
    const value = openJson('contact', '', row, null); if (!value) continue;
    if (row.tag !== localTag('contact', value.pseudo)) throw new Error('Contact local chiffré incohérent.');
    values.push(value);
  }
  return normalizeContacts(values);
}
export async function deleteContact(pseudo) { requireUnlocked(); return validStringKey(pseudo) ? tx('contacts_secure', 'readwrite', (s) => s.delete(localTag('contact', pseudo))) : false; }

export async function getSetting(key, def) {
  requireUnlocked(); if (!validStringKey(key)) return def;
  const tag = localTag('setting', key); const r = await tx('settings_secure', 'readonly', (s) => reqValue(s.get(tag)));
  return r ? openTagged('setting', 'setting', r, key) : def;
}
export async function setSetting(key, value) {
  requireUnlocked(); if (!validStringKey(key)) throw new Error('Clé de réglage locale invalide.');
  return tx('settings_secure', 'readwrite', (s) => s.put(sealTagged('setting', 'setting', key, value)));
}

export async function saveGroup(g) { requireUnlocked(); const safe = normalizeGroups([g])[0]; if (!safe) throw new Error('Groupe local invalide.'); return tx('groups_secure', 'readwrite', (s) => s.put(sealTagged('group', 'group', safe.groupId, safe))); }
export async function getGroups() {
  requireUnlocked(); const rows = asArray(await tx('groups_secure', 'readonly', (s) => reqValue(s.getAll())));
  const vals = [];
  for (const row of rows) {
    const value = openJson('group', '', row, null); if (!value) continue;
    if (row.tag !== localTag('group', value.groupId)) throw new Error('Groupe local chiffré incohérent.');
    vals.push(value);
  }
  return normalizeGroups(vals);
}
export async function getGroup(groupId) {
  requireUnlocked(); if (!validStringKey(groupId)) return null;
  const r = await tx('groups_secure', 'readonly', (s) => reqValue(s.get(localTag('group', groupId))));
  return r ? normalizeGroup(openTagged('group', 'group', r, groupId)) : null;
}
export async function deleteGroup(groupId) { requireUnlocked(); return validStringKey(groupId) ? tx('groups_secure', 'readwrite', (s) => s.delete(localTag('group', groupId))) : false; }

// ---------------------------------------------------------------------------
// Messages
// ---------------------------------------------------------------------------
export async function saveMessage(msg) {
  requireUnlocked(); const record = await sealMessageRecord(msg, false);
  return tx('messages_secure', 'readwrite', (s) => reqValue(s.add(record)));
}
export async function getMessages(peer) {
  requireUnlocked(); if (!validStringKey(peer)) return [];
  const rows = asArray(await tx('messages_secure', 'readonly', (s) => reqValue(s.index('byPeerTag').getAll(localTag('peer', peer)))));
  return normalizeMessages(rows.map(openMessageRecord).filter(Boolean));
}
export async function getAllMessages() {
  requireUnlocked(); const rows = asArray(await tx('messages_secure', 'readonly', (s) => reqValue(s.getAll())));
  return normalizeMessages(rows.map(openMessageRecord).filter(Boolean));
}
export async function getAllMessageMetadata() {
  requireUnlocked(); const rows = asArray(await tx('messages_secure', 'readonly', (s) => reqValue(s.getAll())));
  return normalizeMessages(rows.map(openMessageMeta).filter(Boolean));
}
async function getRawMessage(localId) { return tx('messages_secure', 'readonly', (s) => reqValue(s.get(localId))); }

const localMessageLocks = new Map();
async function withMessageLock(localId, work) {
  const key = `messagerie-vault-message:${localId}`;
  if (globalThis.navigator?.locks?.request) return navigator.locks.request(key, work);
  const prev = localMessageLocks.get(key) || Promise.resolve();
  let release; const gate = new Promise((r) => { release = r; }); const chain = prev.then(() => gate); localMessageLocks.set(key, chain);
  await prev; try { return await work(); } finally { release(); if (localMessageLocks.get(key) === chain) localMessageLocks.delete(key); }
}

export async function updateMessage(localId, patch) {
  requireUnlocked(); if (!validMessageKey(localId)) return false;
  return withMessageLock(localId, async () => {
    const raw = await getRawMessage(localId); if (!raw) return false;
    const meta = openMessageMeta(raw); const nextMeta = { ...meta, ...patch, localId };
    const patchHasData = Object.prototype.hasOwnProperty.call(patch || {}, 'data') || Object.prototype.hasOwnProperty.call(patch || {}, 'blob');
    let record;
    if (patchHasData) record = await sealMessageRecord(nextMeta, true);
    else {
      const dataLess = { ...nextMeta }; delete dataLess.data; delete dataLess.blob; delete dataLess.localId;
      const s = sealJson('message-meta', '', dataLess);
      record = { ...raw, peerTag: localTag('peer', nextMeta.peer), nonce: s.nonce, box: s.box };
    }
    await tx('messages_secure', 'readwrite', (s) => s.put(record)); return true;
  });
}

export async function mutateMessage(localId, mutator) {
  requireUnlocked(); if (!validMessageKey(localId) || typeof mutator !== 'function') return false;
  return withMessageLock(localId, async () => {
    const raw = await getRawMessage(localId); if (!raw) return false;
    const current = openMessageRecord(raw);
    const candidate = mutator(current) || current;
    const safe = normalizeMessage(candidate); if (!safe) return false;
    const binaryUnchanged = safe.data === current.data && safe.blob === current.blob;
    let sealedRecord;
    if (binaryUnchanged) {
      const meta = { ...safe }; delete meta.localId; delete meta.data; delete meta.blob;
      const sealed = sealJson('message-meta', '', meta);
      sealedRecord = { ...raw, peerTag: localTag('peer', safe.peer), nonce: sealed.nonce, box: sealed.box };
    } else {
      sealedRecord = await sealMessageRecord(safe, true);
    }
    await tx('messages_secure', 'readwrite', (s) => s.put(sealedRecord)); return true;
  });
}

export async function markConversationRead(peer) {
  requireUnlocked(); if (!validStringKey(peer)) return false;
  const rows = asArray(await tx('messages_secure', 'readonly', (s) => reqValue(s.index('byPeerTag').getAll(localTag('peer', peer)))));
  const changed = [];
  for (const raw of rows) {
    const meta = openMessageMeta(raw); if (meta.read) continue;
    const next = { ...meta, read: true }; delete next.localId;
    const sealed = sealJson('message-meta', '', next);
    changed.push({ ...raw, nonce: sealed.nonce, box: sealed.box });
  }
  if (!changed.length) return true;
  return tx('messages_secure', 'readwrite', (s) => { for (const r of changed) s.put(r); });
}
export async function deleteMessage(localId) { requireUnlocked(); return validMessageKey(localId) ? tx('messages_secure', 'readwrite', (s) => s.delete(localId)) : false; }
export async function deleteExpiredMessages(now = Date.now()) {
  requireUnlocked(); const rows = asArray(await tx('messages_secure', 'readonly', (s) => reqValue(s.getAll())));
  const ids = [];
  for (const r of rows) { const m = openMessageMeta(r); if (Number(m.expiresAt || 0) > 0 && Number(m.expiresAt) <= now) ids.push(r.localId); }
  if (!ids.length) return 0;
  await tx('messages_secure', 'readwrite', (s) => { for (const id of ids) s.delete(id); }); return ids.length;
}
export async function deleteConversation(peer) {
  requireUnlocked(); if (!validStringKey(peer)) return false;
  const rows = asArray(await tx('messages_secure', 'readonly', (s) => reqValue(s.index('byPeerTag').getAll(localTag('peer', peer)))));
  await tx('messages_secure', 'readwrite', (s) => { for (const r of rows) s.delete(r.localId); });
  await deleteContact(peer); return true;
}
export async function deleteGroupConversation(groupId) {
  requireUnlocked(); if (!validStringKey(groupId)) return false;
  const rows = asArray(await tx('messages_secure', 'readonly', (s) => reqValue(s.index('byPeerTag').getAll(localTag('peer', groupId)))));
  await tx('messages_secure', 'readwrite', (s) => { for (const r of rows) s.delete(r.localId); });
  await deleteGroup(groupId); return true;
}

// ---------------------------------------------------------------------------
// Export / import d'historique (toujours chiffré une seconde fois par la
// passphrase d'export dans crypto.js ; le coffre local n'est jamais exporté).
// ---------------------------------------------------------------------------
function abToB64(ab) { const bytes = new Uint8Array(ab); let bin = ''; const CH = 0x8000; for (let i = 0; i < bytes.length; i += CH) bin += String.fromCharCode(...bytes.subarray(i, i + CH)); return btoa(bin); }
function b64ToAb(b64) { const bin = atob(b64); const out = new Uint8Array(bin.length); for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i); return out.buffer; }
function encMsg(m) { const o = { ...m }; if (o.data) o.data = Array.isArray(o.data) ? { __chunks: o.data.map(abToB64) } : { __ab: abToB64(o.data) }; delete o.blob; return o; }
function decMsg(o) { const m = { ...o }; if (m.data?.__ab) m.data = b64ToAb(m.data.__ab); else if (m.data?.__chunks) m.data = m.data.__chunks.map(b64ToAb); return m; }


export async function collectFullVaultBackup() {
  requireUnlocked();
  const identity = await getIdentity();
  if (!identity?.pseudo || !identity?.publicKey || !identity?.privateKey) throw new Error('Identité locale absente ou invalide.');
  const history = await collectHistory();
  return {
    v: 1,
    type: 'messagerie-vault-backup',
    exportedAt: Date.now(),
    identity,
    history,
    note: 'Les credentials WebAuthn et la DEK locale ne sont pas exportés.',
  };
}

export async function restoreFullVaultBackup(payload) {
  requireUnlocked();
  if (payload?.v !== 1 || payload?.type !== 'messagerie-vault-backup') throw new Error('Sauvegarde complète incompatible.');
  const identity = payload.identity;
  if (!identity?.pseudo || !identity?.publicKey || !identity?.privateKey) throw new Error('Identité de sauvegarde invalide.');
  await saveIdentity(identity);
  await restoreHistory(payload.history);
  return true;
}

export async function collectHistory() {
  const [contacts, groups, messages] = await Promise.all([getContacts(), getGroups(), getAllMessages()]);
  const settingsKeys = ['notifications', 'readReceipts', 'darkMode', 'fontScale', 'highContrast', 'blockedPseudos', 'shoppingItems', 'bubbleColor', 'uiStyle', 'lastExportAt', 'pinnedConversations', 'archivedConversations', 'viewOnceConversations', 'remoteDeletionIds'];
  const settings = {}; for (const k of settingsKeys) settings[k] = await getSetting(k, undefined);
  return { v: 1, exportedAt: Date.now(), contacts, groups, settings, messages: messages.map(encMsg) };
}

export async function restoreHistory(data) {
  requireUnlocked();
  const contacts = normalizeContacts(data?.contacts);
  const groups = normalizeGroups(data?.groups);
  const messages = [];
  for (const raw of asArray(data?.messages)) { if (!isRecord(raw)) continue; const m = normalizeMessage(decMsg(raw)); if (m) { delete m.localId; messages.push(await sealMessageRecord(m, false)); } }
  const settings = isRecord(data?.settings) ? Object.entries(data.settings).filter(([k, v]) => validStringKey(k) && v !== undefined).map(([k, v]) => sealTagged('setting', 'setting', k, v)) : [];
  const contactRows = contacts.map((c) => sealTagged('contact', 'contact', c.pseudo, c));
  const groupRows = groups.map((g) => sealTagged('group', 'group', g.groupId, g));
  const db = await openDb();
  await new Promise((resolve, reject) => {
    const t = db.transaction(['messages_secure', 'contacts_secure', 'groups_secure', 'settings_secure'], 'readwrite');
    for (const n of ['messages_secure', 'contacts_secure', 'groups_secure', 'settings_secure']) t.objectStore(n).clear();
    for (const r of contactRows) t.objectStore('contacts_secure').put(r);
    for (const r of groupRows) t.objectStore('groups_secure').put(r);
    for (const r of messages) t.objectStore('messages_secure').add(r);
    for (const r of settings) t.objectStore('settings_secure').put(r);
    t.oncomplete = resolve; t.onerror = () => reject(t.error); t.onabort = () => reject(t.error);
  });
}

// Suppression de compte locale : efface données chiffrées + ancien plaintext +
// métadonnées du coffre. La clé maître mémoire est ensuite zéroïsée.
export async function wipeAll() {
  const db = await openDb();
  await new Promise((resolve, reject) => {
    const t = db.transaction([...LEGACY_STORES, ...SECURE_STORES, VAULT_STORE], 'readwrite');
    for (const s of [...LEGACY_STORES, ...SECURE_STORES, VAULT_STORE]) t.objectStore(s).clear();
    t.oncomplete = resolve; t.onerror = () => reject(t.error); t.onabort = () => reject(t.error);
  });
  clearAutoSecret();
  lockVaultMemory();
}
